/**
 * Admissions save orchestrator.
 *
 * Runs the save sequence that calls each existing tab's POST endpoint in
 * dependency order. Not transactional — we chose this intentionally in the
 * design convo so admissions reuses each tab's function (create-grade-level
 * in Academics, create-parent in People, etc.) exactly as if an admin had
 * clicked through every tab by hand.
 *
 * The caller supplies a `progress(step, status, detail)` callback that we
 * use to drive the progress UI in the Review step. Each step's status
 * goes pending → running → done/failed.
 *
 * Every completed step is recorded in the returned `created` object so a
 * partial failure can be retried without duplicating work — call
 * orchestrate() again with the prior `created` passed in.
 */

import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";

function getUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { return null; }
}
function authHeaders(extra = {}) {
  const u = getUser();
  return {
    "Content-Type": "application/json",
    "X-User-Role":  u?.role || "",
    "X-Username":   u?.username || "",
    ...extra,
  };
}

async function postJson(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res));
  return res.json();
}

async function postMultipart(path, fields) {
  const fd = new FormData();
  for (const [k, v] of Object.entries(fields)) {
    if (v == null) continue;
    fd.append(k, v);
  }
  const u = getUser();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "X-User-Role": u?.role || "",
      "X-Username":  u?.username || "",
    },
    body: fd,
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, `Upload failed (${res.status})`));
  return res.json();
}

function normalizeName(s) {
  return String(s || "")
    .replace(/\s*·\s*Active\s*$/i, "")   // drop the "· Active" UI tag on SY/semester labels
    .trim().toLowerCase().replace(/\s+/g, "");
}

/** Resolve a TypeableCombo value to an id, creating the record if needed.
 *  When the user typed a "new" name that actually matches an existing item
 *  (case-insensitive, whitespace-normalized), reuse that item's id — the
 *  backend's uniqueness check would 409 otherwise. */
async function resolveCombo(value, { createPath, createBody, created, key, existing = [] }) {
  if (!value) return null;
  if (value.id != null) return value.id;
  if (value.newName != null && value.newName.trim()) {
    if (created[key]) return created[key];     // retry-safe
    const norm = normalizeName(value.newName);
    const hit = existing.find(it => normalizeName(it.label) === norm);
    if (hit) {
      created[key] = hit.id;
      return hit.id;
    }
    const row = await postJson(createPath, createBody(value.newName.trim()));
    created[key] = row.id;
    return row.id;
  }
  return null;
}

/**
 * Main orchestrator. Returns `{ created }` with every id it produced so
 * the caller can hand it back on retry and skip already-done work.
 */
export async function runAdmissionsSave({
  student, parents, enrollment, tuition, discounts, documents,
  existing = {},
  prior = {},
  progress = () => {},
}) {
  const created = { ...prior };
  const mark = (step, status, detail) => progress(step, status, detail);

  // ── Academic structure (resolve or create as needed) ──────────────
  mark("academic", "running");

  const schoolYearId = await resolveCombo(enrollment.schoolYear, {
    createPath: "/api/school-years",
    createBody: (name) => ({ label: name }),
    created, key: "schoolYearId",
    existing: existing.schoolYearItems,
  });

  const semesterId = await resolveCombo(enrollment.semester, {
    createPath: "/api/semesters",
    createBody: (name) => ({ schoolYearId, label: name }),
    created, key: "semesterId",
    existing: existing.semesterItems,
  });

  const gradeLevelId = await resolveCombo(enrollment.gradeLevel, {
    createPath: "/api/grade-levels",
    createBody: (name) => ({ name }),
    created, key: "gradeLevelId",
    existing: existing.gradeLevelItems,
  });

  const sectionId = await resolveCombo(enrollment.section, {
    createPath: "/api/sections",
    createBody: (name) => ({ name, schoolYearId, gradeLevelId }),
    created, key: "sectionId",
    existing: existing.sectionItems,
  });

  mark("academic", "done", { schoolYearId, semesterId, gradeLevelId, sectionId });

  // ── Fee structure (before enrollment so the auto-created invoice
  //    uses the right amount). If the admin picked an existing fee
  //    structure we reuse it; otherwise create one for (grade, SY). ──
  mark("tuition", "running");
  let feeStructureId = tuition.existingId || created.feeStructureId;
  if (!feeStructureId && tuition.isNew) {
    const n = (v) => (v === "" || v == null ? 0 : Number(v) || 0);
    const total = (n(tuition.tuitionFee) + n(tuition.miscFee) + n(tuition.booksFee)).toFixed(2);
    const row = await postJson("/api/fee-structures", {
      gradeLevelId, schoolYearId,
      tuitionFee:    tuition.tuitionFee    || null,
      miscFee:       tuition.miscFee       || null,
      booksFee:      tuition.booksFee      || null,
      enrollmentFee: tuition.enrollmentFee || null,
      months:        tuition.months        || 9,
      totalAmount:   total,
      breakdown:     tuition.breakdown     || null,
    });
    feeStructureId = row.id;
    created.feeStructureId = feeStructureId;
  }
  mark("tuition", "done", { feeStructureId });

  // ── Student ───────────────────────────────────────────────────────
  mark("student", "running");
  if (!created.studentId) {
    const row = await postJson("/api/students", {
      studentNumber: student.studentNumber,
      firstName:     student.firstName,
      middleName:    student.middleName || null,
      lastName:      student.lastName,
      birthdate:     student.birthdate || null,
      sex:           student.sex || null,
      contact:       student.contact || null,
      address:       student.address || null,
      status:        "PENDING",  // EnrollmentController flips this to ENROLLED
    });
    created.studentId = row.id;
  }
  const studentId = created.studentId;
  mark("student", "done", { studentId });

  // ── Parents + links. Parents use a retry-safe index per row so a
  //    re-run doesn't double-create anyone. ────────────────────────
  mark("parents", "running");
  created.parentIds = created.parentIds || [];
  created.parentLinkIds = created.parentLinkIds || [];
  for (let i = 0; i < (parents || []).length; i++) {
    const p = parents[i];
    let parentId = created.parentIds[i];
    if (!parentId) {
      if (p.existingId) {
        parentId = p.existingId;
      } else {
        const row = await postJson("/api/parents", {
          firstName:  p.firstName,
          lastName:   p.lastName,
          contact:    p.contact    || null,
          email:      p.email      || null,
          occupation: p.occupation || null,
          address:    p.address    || null,
        });
        parentId = row.id;
      }
      created.parentIds[i] = parentId;
    }
    if (!created.parentLinkIds[i]) {
      try {
        const link = await postJson("/api/parent-student", {
          parentId, studentId,
          relationship: p.relationship || "Guardian",
          primary: !!p.primary,
        });
        created.parentLinkIds[i] = link.id;
      } catch (e) {
        // Duplicate (parentId, studentId) links are rejected by the DB.
        // Treat as already-linked (retry-safe) and move on.
        if (String(e.message).toLowerCase().includes("duplicate")
            || String(e.message).toLowerCase().includes("already")) {
          created.parentLinkIds[i] = "existing";
        } else {
          throw e;
        }
      }
    }
  }
  mark("parents", "done", { count: (parents || []).length });

  // ── Enrollment (auto-creates UNPAID invoice since the fee structure
  //    was just created/selected). ─────────────────────────────────
  mark("enrollment", "running");
  if (!created.enrollmentId) {
    const row = await postJson("/api/enrollments", {
      studentId, schoolYearId, semesterId, sectionId,
      status: "ENROLLED",
    });
    created.enrollmentId = row.id;
  }
  mark("enrollment", "done", { enrollmentId: created.enrollmentId });

  // ── Discounts ─────────────────────────────────────────────────────
  mark("discounts", "running");
  created.discountIds = created.discountIds || [];
  for (let i = 0; i < (discounts || []).length; i++) {
    if (created.discountIds[i]) continue;
    const d = discounts[i];
    const row = await postJson("/api/student-discounts", {
      studentId, schoolYearId,
      type: d.type,
      percentage: Number(d.percentage) || 0,
      notes: d.notes || null,
      customName: d.type === "OTHERS" ? (d.customName || "").trim() || null : null,
      // Defaults preserve the old tuition-only behaviour when the wizard is
      // called by something that hasn't set the flags yet.
      appliesTuition: d.appliesTuition != null ? !!d.appliesTuition : true,
      appliesMisc:    !!d.appliesMisc,
      appliesBooks:   !!d.appliesBooks,
    });
    created.discountIds[i] = row.id;
  }
  mark("discounts", "done", { count: (discounts || []).length });

  // ── Documents (multipart to S3 via StudentDocumentController). ──
  mark("documents", "running");
  created.documentIds = created.documentIds || [];
  for (let i = 0; i < (documents || []).length; i++) {
    if (created.documentIds[i]) continue;
    const d = documents[i];
    if (!d.file) continue;
    const row = await postMultipart(`/api/students/${studentId}/documents`, {
      file:  d.file,
      label: d.label || d.file.name || "Document",
    });
    created.documentIds[i] = row.id;
  }
  mark("documents", "done", { count: (documents || []).length });

  return { created };
}
